import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Checkbox } from '../../components/ui/checkbox';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../../components/ui/dialog';
import {
  Users, MagnifyingGlass, Bell, KeyReturn, Tag as TagIcon, UsersThree,
  Pencil, Trash, Plus, CircleNotch, ClockClockwise, X, EnvelopeSimple, CurrencyEur, CloudArrowDown, FileText, PaperPlaneTilt,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const MigrationEmailBanner = () => {
  const [status, setStatus] = useState(null);
  const [sending, setSending] = useState(false);
  const API = process.env.REACT_APP_BACKEND_URL;

  const fetchStatus = useCallback(() => {
    axios.get(`${API}/api/admin/migrated-accounts-status`, { withCredentials: true })
      .then(({ data }) => setStatus(data)).catch(() => {});
  }, [API]);
  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  if (!status || status.remaining <= 0) return null;

  const sendAll = async () => {
    if (!status.smtp_configured) { toast.error('Configurez d\'abord le SMTP (Réglages → Emails)'); return; }
    if (!window.confirm(`Envoyer l'email de bienvenue avec lien de création de mot de passe à ${status.remaining} client(s) migré(s) ?\n\nChaque client recevra un lien personnel valable 7 jours. Durée estimée : quelques minutes.`)) return;
    setSending(true);
    let total = 0, fails = 0, guard = 0;
    try {
      while (guard < 80) {
        const { data } = await axios.post(`${API}/api/admin/send-migration-emails?batch=15`, {}, { withCredentials: true, timeout: 180000 });
        total += data.sent; fails += data.failed; guard++;
        if (data.remaining <= 0 || (data.sent === 0 && data.failed === 0)) break;
        toast.info(`Envoi en cours… ${total} envoyé(s), ${data.remaining} restant(s)`, { duration: 3000 });
        if (data.failed > 0 && data.sent === 0) break;
      }
      if (fails === 0) toast.success(`${total} email(s) de bienvenue envoyé(s) ✅`, { duration: 8000 });
      else toast.warning(`${total} envoyé(s), ${fails} échec(s) — voir le journal des emails`);
      fetchStatus();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur pendant l\'envoi');
    } finally { setSending(false); }
  };

  return (
    <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 flex items-center justify-between flex-wrap gap-3" data-testid="migration-email-banner">
      <div>
        <p className="font-semibold text-blue-900 text-sm">✉️ {status.remaining} compte(s) migré(s) sans mot de passe n'ont pas encore reçu l'email de bienvenue</p>
        <p className="text-xs text-blue-700 mt-0.5">Chaque client recevra un lien personnel (valable 7 jours) pour définir son nouveau mot de passe.{status.already_sent > 0 ? ` Déjà envoyés : ${status.already_sent}.` : ''}</p>
      </div>
      <Button onClick={sendAll} disabled={sending} className="gap-2 bg-blue-600 hover:bg-blue-700 text-white" data-testid="send-migration-emails-btn">
        {sending ? <CircleNotch size={16} className="animate-spin" /> : <PaperPlaneTilt size={16} />}
        {sending ? 'Envoi en cours…' : 'Envoyer les emails de bienvenue'}
      </Button>
    </div>
  );
};

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CustomersPageV2 = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [groups, setGroups] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [search, setSearch] = useState('');
  const [filterGroup, setFilterGroup] = useState('all');
  const [sortBy, setSortBy] = useState('ca'); // 'rank' | 'orders' | 'ca' | 'avg'

  // Dialogs
  const [groupsDialogOpen, setGroupsDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [notifyDialogOpen, setNotifyDialogOpen] = useState(false);
  const [groupFormOpen, setGroupFormOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);

  // Notify form
  const [notifyForm, setNotifyForm] = useState({ title: '', message: '', link: '' });

  // Import WooCommerce (full live API migration)
  const [importing, setImporting] = useState(false);
  const doImportWoo = async () => {
    if (!window.confirm("Lancer la MIGRATION COMPLÈTE depuis l'API WooCommerce de magere-viandes.fr ?\n\n• Produits + images (téléchargées en local)\n• Clients (176)\n• Commandes (130 + invités liés par email)\n\nDurée : 5-10 min. Re-exécutable en toute sécurité.")) return;
    setImporting(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/wc-api-import`, {}, { withCredentials: true, timeout: 900000 });
      const p = data.products || {}; const u = data.customers || {}; const o = data.orders || {};
      toast.success(
        `Migration WC complète · Produits : ${p.created || 0}+${p.updated || 0} màj · ` +
        `Clients : ${u.created || 0}+${u.updated || 0} (${data.guest_accounts_created || 0} invités) · ` +
        `Commandes : ${o.created || 0} (${data.orphan_orders_linked || 0} liées par email)`,
        { duration: 12000 },
      );
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de migration");
    } finally { setImporting(false); }
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [c, g] = await Promise.all([
        axios.get(`${API_URL}/api/admin/customers-list`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/customer-groups`, { withCredentials: true }),
      ]);
      setCustomers(c.data || []);
      setGroups(g.data || []);
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally { setLoading(false); }
  }, []);

  const fetchHistory = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/admin/bulk-actions-history`, { withCredentials: true });
      setHistory(data || []);
    } catch {}
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Filtered + sorted
  const filtered = customers
    .filter(c => {
      if (filterGroup === 'none') { if (c.group_id) return false; }
      else if (filterGroup !== 'all' && c.group_id !== filterGroup) return false;
      if (!search) return true;
      const s = search.toLowerCase();
      return (c.email || '').toLowerCase().includes(s)
          || (c.name || '').toLowerCase().includes(s)
          || (c.phone || '').toLowerCase().includes(s);
    })
    .sort((a, b) => {
      const avgA = (a.total_orders > 0) ? (a.total_spent || 0) / a.total_orders : 0;
      const avgB = (b.total_orders > 0) ? (b.total_spent || 0) / b.total_orders : 0;
      if (sortBy === 'orders') return (b.total_orders || 0) - (a.total_orders || 0);
      if (sortBy === 'ca' || sortBy === 'rank') return (b.total_spent || 0) - (a.total_spent || 0);
      if (sortBy === 'avg') return avgB - avgA;
      return 0;
    });

  const selectedCount = selectedIds.size;
  const allFilteredSelected = filtered.length > 0 && filtered.every(c => selectedIds.has(c.id));

  const toggleAll = () => {
    const next = new Set(selectedIds);
    if (allFilteredSelected) {
      filtered.forEach(c => next.delete(c.id));
    } else {
      filtered.forEach(c => next.add(c.id));
    }
    setSelectedIds(next);
  };

  const toggleOne = (id) => {
    const next = new Set(selectedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedIds(next);
  };

  const groupById = (id) => groups.find(g => g.id === id);

  // ── Bulk actions ──
  const doNotify = async () => {
    if (!notifyForm.title.trim() || !notifyForm.message.trim()) { toast.error('Titre + message requis'); return; }
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/customers/bulk-notify`,
        { user_ids: [...selectedIds], ...notifyForm }, { withCredentials: true });
      toast.success(data.message);
      setNotifyDialogOpen(false);
      setNotifyForm({ title: '', message: '', link: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const doPasswordReset = async () => {
    if (!window.confirm(`Envoyer un lien de réinitialisation à ${selectedCount} client(s) ?`)) return;
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/customers/bulk-password-reset`,
        { user_ids: [...selectedIds] }, { withCredentials: true });
      toast.success(data.message);
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const doAssignGroup = async (groupId) => {
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/customers/bulk-assign-group`,
        { user_ids: [...selectedIds], group_id: groupId || null }, { withCredentials: true });
      toast.success(`${data.modified} client(s) mis à jour`);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const doAddTag = async () => {
    const tag = window.prompt('Nom du tag à ajouter ?');
    if (!tag) return;
    try {
      const { data } = await axios.post(`${API_URL}/api/admin/customers/bulk-add-tags`,
        { user_ids: [...selectedIds], tags: [tag] }, { withCredentials: true });
      toast.success(`${data.modified} client(s) marqué(s) avec "${tag}"`);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  // ── Groups CRUD ──
  const saveGroup = async (g) => {
    try {
      if (g.id) {
        await axios.put(`${API_URL}/api/admin/customer-groups/${g.id}`, g, { withCredentials: true });
      } else {
        await axios.post(`${API_URL}/api/admin/customer-groups`, g, { withCredentials: true });
      }
      toast.success('Groupe sauvegardé');
      setGroupFormOpen(false);
      setEditingGroup(null);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const deleteGroup = async (id) => {
    if (!window.confirm('Supprimer ce groupe ?')) return;
    await axios.delete(`${API_URL}/api/admin/customer-groups/${id}`, { withCredentials: true });
    toast.success('Groupe supprimé');
    fetchAll();
  };

  return (
    <div className="space-y-5" data-testid="customers-page-v2">
      <MigrationEmailBanner />
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Clients</h1>
          <p className="text-slate-500 mt-0.5 text-sm">{customers.length} clients · {groups.length} groupes</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={doImportWoo} disabled={importing} className="gap-2" data-testid="import-woo-btn">
            {importing ? <CircleNotch size={16} className="animate-spin" /> : <CloudArrowDown size={16} />}
            {importing ? 'Migration en cours…' : 'Migrer depuis WooCommerce'}
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              const link = document.createElement('a');
              link.href = `${API_URL}/api/admin/customers-export`;
              link.target = '_blank';
              link.rel = 'noopener';
              // Backend auth uses cookie or Bearer header. Backend uses get_current_user which reads cookie or header.
              // Since axios attaches cookie credentials, but link won't carry headers, we rely on cookie auth.
              document.body.appendChild(link); link.click(); document.body.removeChild(link);
            }}
            className="gap-2"
            data-testid="export-csv-btn"
          >
            <FileText size={16} /> Exporter CSV
          </Button>
          <Button variant="outline" onClick={() => { fetchHistory(); setHistoryDialogOpen(true); }} className="gap-2" data-testid="open-history-btn">
            <ClockClockwise size={16} /> Historique
          </Button>
          <Button variant="outline" onClick={() => setGroupsDialogOpen(true)} className="gap-2" data-testid="open-groups-btn">
            <UsersThree size={16} /> Groupes ({groups.length})
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Recherche email, nom, téléphone..." className="pl-9" data-testid="customer-search" />
          </div>
          <Select value={filterGroup} onValueChange={setFilterGroup}>
            <SelectTrigger className="w-[200px]" data-testid="filter-group-select"><SelectValue placeholder="Tous les groupes" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les groupes</SelectItem>
              <SelectItem value="none">Sans groupe</SelectItem>
              {groups.map(g => <SelectItem key={g.id} value={g.id}>{g.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Bulk action toolbar */}
      {selectedCount > 0 && (
        <div className="sticky top-0 z-20 bg-blue-50 border border-blue-200 rounded-xl p-3 flex flex-wrap items-center gap-2" data-testid="bulk-toolbar">
          <span className="font-bold text-blue-900 px-2">{selectedCount} sélectionné{selectedCount > 1 ? 's' : ''}</span>
          <Button size="sm" onClick={() => setNotifyDialogOpen(true)} className="gap-1.5 bg-[#0052FF] hover:bg-[#0040CC]" data-testid="bulk-notify-btn">
            <Bell size={14} /> Notifier
          </Button>
          <Button size="sm" variant="outline" onClick={doPasswordReset} className="gap-1.5" data-testid="bulk-reset-btn">
            <KeyReturn size={14} /> Reset mot de passe
          </Button>
          <Button size="sm" variant="outline" onClick={doAddTag} className="gap-1.5" data-testid="bulk-tag-btn">
            <TagIcon size={14} /> Ajouter un tag
          </Button>
          <Select onValueChange={(v) => doAssignGroup(v === '__none__' ? null : v)}>
            <SelectTrigger className="w-[200px] h-9" data-testid="bulk-group-select"><SelectValue placeholder="→ Assigner un groupe" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__none__">Retirer du groupe</SelectItem>
              {groups.map(g => <SelectItem key={g.id} value={g.id}>{g.name} ({g.discount_percent}%)</SelectItem>)}
            </SelectContent>
          </Select>
          <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())}><X size={14} /></Button>
        </div>
      )}

      {/* Customers list */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {loading ? (
            <div className="p-8 text-center"><CircleNotch className="animate-spin mx-auto text-slate-400" size={28} /></div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-slate-500">Aucun client</div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-slate-50">
                  <th className="p-3 w-10">
                    <Checkbox checked={allFilteredSelected} onCheckedChange={toggleAll} data-testid="select-all-checkbox" />
                  </th>
                  <th className="p-3 text-left">
                    <button onClick={() => setSortBy('rank')} className={`hover:underline ${sortBy === 'rank' ? 'font-bold text-[#0052FF]' : ''}`} data-testid="sort-rank">#</button>
                  </th>
                  <th className="p-3 text-left">Type</th>
                  <th className="p-3 text-left">Client</th>
                  <th className="p-3 text-left">Groupe / Tags</th>
                  <th className="p-3 text-right">
                    <button onClick={() => setSortBy('orders')} className={`hover:underline ${sortBy === 'orders' ? 'font-bold text-[#0052FF]' : ''}`} data-testid="sort-orders">Commandes ↓</button>
                  </th>
                  <th className="p-3 text-right">
                    <button onClick={() => setSortBy('ca')} className={`hover:underline ${sortBy === 'ca' ? 'font-bold text-[#0052FF]' : ''}`} data-testid="sort-ca">CA ↓</button>
                  </th>
                  <th className="p-3 text-right">
                    <button onClick={() => setSortBy('avg')} className={`hover:underline ${sortBy === 'avg' ? 'font-bold text-[#0052FF]' : ''}`} data-testid="sort-avg">Panier moyen ↓</button>
                  </th>
                  <th className="p-3 text-left">Dernière commande</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c, idx) => {
                  const g = groupById(c.group_id);
                  const selected = selectedIds.has(c.id);
                  const isPro = g && /pro|restaurant|wholesale/i.test(g.name || '');
                  const avgBasket = (c.total_orders > 0) ? (c.total_spent || 0) / c.total_orders : 0;
                  return (
                    <tr key={c.id} className={`border-b hover:bg-slate-50 cursor-pointer ${selected ? 'bg-blue-50/40' : ''}`} data-testid={`customer-row-${c.id}`}
                        onClick={(e) => {
                          if (e.target.closest('[data-no-nav]')) return;
                          navigate(`/admin/customers/${c.id}`);
                        }}>
                      <td className="p-3" data-no-nav>
                        <Checkbox checked={selected} onCheckedChange={() => toggleOne(c.id)} onClick={(e) => e.stopPropagation()} data-testid={`customer-cb-${c.id}`} />
                      </td>
                      <td className="p-3 text-slate-400 text-[12px] font-semibold tabular-nums">{idx + 1}</td>
                      <td className="p-3">
                        {isPro ? (
                          <Badge className="bg-indigo-100 text-indigo-700 border border-indigo-300 text-[10px] font-bold" data-testid={`type-pro-${c.id}`}>
                            PRO
                          </Badge>
                        ) : (
                          <Badge className="bg-slate-100 text-slate-600 border border-slate-200 text-[10px]" data-testid={`type-particulier-${c.id}`}>
                            Particulier
                          </Badge>
                        )}
                      </td>
                      <td className="p-3">
                        <div className="font-medium">{c.name}</div>
                        <div className="text-xs text-slate-500 flex items-center gap-1.5"><EnvelopeSimple size={12} /> {c.email}</div>
                        {c.phone && <div className="text-xs text-slate-500">{c.phone}</div>}
                      </td>
                      <td className="p-3">
                        <div className="flex flex-wrap gap-1">
                          {g && <Badge style={{ backgroundColor: g.color || '#0052FF', color: '#fff' }} className="text-[10px]">{g.name} -{g.discount_percent}%</Badge>}
                          {(c.tags || []).map(t => <Badge key={t} variant="secondary" className="text-[10px]">{t}</Badge>)}
                        </div>
                      </td>
                      <td className="p-3 text-right tabular-nums">{c.total_orders}</td>
                      <td className="p-3 text-right font-semibold tabular-nums">{(c.total_spent || 0).toFixed(2)} €</td>
                      <td className="p-3 text-right text-slate-600 tabular-nums">{avgBasket.toFixed(2)} €</td>
                      <td className="p-3 text-xs text-slate-500">
                        {c.last_order ? new Date(c.last_order).toLocaleDateString('fr-FR') : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* ──────── Dialog: Notify ──────── */}
      <Dialog open={notifyDialogOpen} onOpenChange={setNotifyDialogOpen}>
        <DialogContent data-testid="notify-dialog">
          <DialogHeader>
            <DialogTitle>Notifier {selectedCount} client{selectedCount > 1 ? 's' : ''}</DialogTitle>
            <DialogDescription>La notification apparaîtra dans la cloche des clients sélectionnés.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Titre *</Label>
              <Input value={notifyForm.title} onChange={e => setNotifyForm(f => ({ ...f, title: e.target.value }))} placeholder="Ex: Nouvelle gamme disponible" data-testid="notify-title" />
            </div>
            <div>
              <Label>Message *</Label>
              <Textarea value={notifyForm.message} onChange={e => setNotifyForm(f => ({ ...f, message: e.target.value }))} placeholder="Votre message..." rows={4} data-testid="notify-message" />
            </div>
            <div>
              <Label>Lien (optionnel)</Label>
              <Input value={notifyForm.link} onChange={e => setNotifyForm(f => ({ ...f, link: e.target.value }))} placeholder="/promo" data-testid="notify-link" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNotifyDialogOpen(false)}>Annuler</Button>
            <Button onClick={doNotify} className="bg-[#0052FF] hover:bg-[#0040CC]" data-testid="send-notify-btn">Envoyer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ──────── Dialog: Groups CRUD ──────── */}
      <Dialog open={groupsDialogOpen} onOpenChange={setGroupsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Groupes de clients</DialogTitle>
            <DialogDescription>Créez des groupes (PRO, Restaurant…) avec un pourcentage de remise appliqué automatiquement.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {groups.map(g => (
              <div key={g.id} className="flex items-center gap-3 border rounded-lg p-3" data-testid={`group-row-${g.id}`}>
                <div className="w-3 h-10 rounded" style={{ backgroundColor: g.color || '#0052FF' }} />
                <div className="flex-1">
                  <p className="font-medium">{g.name}</p>
                  {g.description && <p className="text-xs text-slate-500">{g.description}</p>}
                </div>
                <Badge>-{g.discount_percent}%</Badge>
                <Button size="icon" variant="ghost" onClick={() => { setEditingGroup(g); setGroupFormOpen(true); }} data-testid={`edit-group-${g.id}`}><Pencil size={14} /></Button>
                <Button size="icon" variant="ghost" onClick={() => deleteGroup(g.id)} className="text-red-600" data-testid={`delete-group-${g.id}`}><Trash size={14} /></Button>
              </div>
            ))}
            <Button onClick={() => { setEditingGroup({ name: '', discount_percent: 0, color: '#0052FF', description: '' }); setGroupFormOpen(true); }} className="w-full gap-2" data-testid="add-group-btn">
              <Plus size={16} /> Nouveau groupe
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={groupFormOpen} onOpenChange={setGroupFormOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editingGroup?.id ? 'Modifier' : 'Nouveau'} groupe</DialogTitle></DialogHeader>
          {editingGroup && (
            <div className="space-y-3">
              <div>
                <Label>Nom *</Label>
                <Input value={editingGroup.name} onChange={e => setEditingGroup({ ...editingGroup, name: e.target.value })} placeholder="Ex: PRO, Restaurant, VIP" data-testid="group-name" />
              </div>
              <div>
                <Label>% de remise</Label>
                <Input type="number" min="0" max="100" step="0.5" value={editingGroup.discount_percent}
                  onChange={e => setEditingGroup({ ...editingGroup, discount_percent: parseFloat(e.target.value || 0) })}
                  data-testid="group-discount" />
              </div>
              <div>
                <Label>Couleur</Label>
                <Input type="color" value={editingGroup.color || '#0052FF'} onChange={e => setEditingGroup({ ...editingGroup, color: e.target.value })} data-testid="group-color" />
              </div>
              <div>
                <Label>Description</Label>
                <Textarea value={editingGroup.description || ''} onChange={e => setEditingGroup({ ...editingGroup, description: e.target.value })} rows={2} data-testid="group-desc" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setGroupFormOpen(false)}>Annuler</Button>
            <Button onClick={() => saveGroup(editingGroup)} className="bg-[#0052FF] hover:bg-[#0040CC]" data-testid="save-group-btn">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ──────── Dialog: History ──────── */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Historique des actions groupées</DialogTitle>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto space-y-2">
            {history.length === 0 ? (
              <p className="text-center text-slate-500 py-6">Aucune action enregistrée</p>
            ) : history.map(h => (
              <div key={h.id} className="border rounded-lg p-3 text-sm" data-testid={`history-${h.id}`}>
                <div className="flex justify-between items-center mb-1">
                  <Badge>{h.action}</Badge>
                  <span className="text-xs text-slate-400">{new Date(h.created_at).toLocaleString('fr-FR')}</span>
                </div>
                <p>par <strong>{h.actor_email}</strong> · {h.target_count} destinataire{h.target_count > 1 ? 's' : ''}</p>
                {h.payload?.title && <p className="text-xs text-slate-500 mt-1">Titre : {h.payload.title}</p>}
                {h.payload?.tags && <p className="text-xs text-slate-500 mt-1">Tags : {h.payload.tags.join(', ')}</p>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CustomersPageV2;
